import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { REVEAL_EASE } from '../core/reveal';
import { RevealWords } from './RevealWords';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const DEFAULT_REACTIONS = ['❤️', '👍', '😮', '✨', '🙏'];

/**
 * MessageBubble — a single chat message, iMessage-snug.
 *   you   → warm neutral fill (var(--surface)), NEVER gold. Right-aligned.
 *   other → transparent fill (var(--bg)) + hairline border. Left-aligned.
 * The tail corner (one tight radius) points to the speaker.
 *
 * Grouping: within a run from the same sender, only the NEWEST bubble shows the
 * avatar + name. Earlier bubbles in the group pass `reserveGutter` so they stay
 * aligned under the avatar column without repeating it.
 *
 * `avatar` takes any node (an <Avatar/>). `reaction` renders a small emoji pill
 * below the bubble. `senderName` shows a quiet label above it.
 */
export interface MessageBubbleProps {
  /** Who sent it. Default 'other'. */
  from?: 'you' | 'other';
  /** Message body. */
  children?: ReactNode;
  /**
   * Renders bouncing dots in place of `children` — the same bubble, same
   * avatar, same row a reply will land in. Big chat apps (iMessage,
   * WhatsApp) never swap a separate "typing" element for a new message
   * element; the dots just become text inside the one bubble that was
   * already there. Swapping elements is what read as a glitchy double-avatar
   * jump here.
   */
  typing?: boolean;
  /** Optional timestamp shown bottom-right. */
  time?: string;
  /** Quiet sender label shown above the bubble (the first in a group; the avatar goes by the last). */
  senderName?: string;
  /** Avatar node (e.g. <Avatar/>) — left gutter for 'other', right gutter for 'you'. */
  avatar?: ReactNode;
  /** Emoji reaction pill shown below the bubble. */
  reaction?: string;
  /** Reserve the 28px avatar gutter without an avatar (earlier group members). */
  reserveGutter?: boolean;
  /** When set, a floating emoji picker appears above the bubble on hover. */
  onReact?: (emoji: string) => void;
  /** Emoji offered by the hover picker. Default ❤️ 👍 😮 ✨ 🙏. */
  reactionOptions?: string[];
  /**
   * How a message that has just arrived lands. 'words': the text fades, rises
   * and un-blurs word by word (for Alma's replies, streamed or not; `children`
   * must be a string). 'bubble': the whole bubble fades and rises in about a
   * quarter of a second (for the traveler's own messages, which should confirm
   * instantly). Leave unset for messages that were already there, such as history.
   * Neither plays under reduced motion.
   */
  reveal?: 'words' | 'bubble';
  /** With reveal 'words': called when all the words so far have finished appearing. */
  onRevealed?: () => void;
  /** 'drop': the avatar slides down into place, as if it moved from the bubble above (when it joins a later bubble of the same reply). Not under reduced motion. */
  avatarMotion?: 'drop';
  style?: CSSProperties;
}

const AVATAR_KEYFRAMES = '@keyframes sk-avatar-drop{from{opacity:0;transform:translateY(-22px)}to{opacity:1;transform:translateY(0)}}';
const BUBBLE_KEYFRAMES = '@keyframes sk-bubble-in{from{opacity:0;transform:translateY(6px);filter:blur(1.5px)}to{opacity:1;transform:translateY(0);filter:blur(0)}}';
export const BUBBLE_REVEAL_MS = 280;

export function MessageBubble({
  from = 'other',
  children,
  typing = false,
  time,
  senderName,
  avatar,
  reaction,
  reserveGutter = false,
  onReact,
  reactionOptions = DEFAULT_REACTIONS,
  reveal,
  onRevealed,
  avatarMotion,
  style,
}: MessageBubbleProps) {
  const isYou = from === 'you';
  const [hover, setHover] = useState(false);
  const [pickerHover, setPickerHover] = useState<string | null>(null);
  // Rises into place once on mount — never retriggers as streamed content
  // fills the same bubble instance (see TryChat's per-message React key).
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const reduceMotion = prefersReducedMotion();
  const wordsReveal = reveal === 'words' && !reduceMotion && typeof children === 'string' && !typing;
  const bubbleReveal = reveal === 'bubble' && !reduceMotion;

  const bubble = (
    <div
      style={{
        maxWidth: '100%',
        padding: 'var(--chat-bubble-pad-y) var(--chat-bubble-pad-x)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--chat-fs)',
        lineHeight: 'var(--chat-lh)',
        color: 'var(--text)',
        background: isYou ? 'var(--surface)' : 'var(--bg)',
        border: '1px solid var(--line)',
        borderRadius: 16,
        borderBottomRightRadius: isYou ? 4 : 16,
        borderBottomLeftRadius: isYou ? 16 : 4,
        overflowWrap: 'anywhere',
        ...(bubbleReveal ? { animation: `sk-bubble-in ${BUBBLE_REVEAL_MS}ms ${REVEAL_EASE} both` } : null),
        ...style,
      }}
      // The animation holds its end state; let go of it once the bubble has landed.
      onAnimationEnd={bubbleReveal ? (e) => { if (e.target === e.currentTarget) e.currentTarget.style.animation = 'none'; } : undefined}
    >
      {bubbleReveal ? <style>{BUBBLE_KEYFRAMES}</style> : null}
      {typing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 2px' }}>
          <style>{'@keyframes sk-typing-bounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-5px)}}'}</style>
          {[0, 150, 300].map((delay) => (
            <span
              key={delay}
              style={{
                width: 7, height: 7, borderRadius: '50%', background: 'var(--text-dim)',
                animation: 'sk-typing-bounce 1.2s infinite', animationDelay: `${delay}ms`,
              }}
            />
          ))}
        </div>
      ) : wordsReveal ? <RevealWords text={children as string} onDone={onRevealed} /> : children}
      {time && !typing ? (
        <div style={{ fontSize: 'var(--chat-fs-time)', marginTop: 4, color: 'var(--text-dim)', textAlign: 'right' }}>{time}</div>
      ) : null}
    </div>
  );

  const reactionPill = reaction ? (
    <span
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-pill)',
        padding: '3px 10px',
        fontSize: 12,
        lineHeight: 1,
        cursor: 'pointer',
        alignSelf: isYou ? 'flex-end' : 'flex-start',
      }}
    >
      {reaction}
    </span>
  ) : null;

  const column = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isYou ? 'flex-end' : 'flex-start',
        gap: 3,
        // Opacity-only, never transform: this box's width/height keeps
        // changing as Alma's reply streams in, and animating a transform
        // at the same time as that reflow reads as jitter, not polish.
        opacity: reduceMotion || bubbleReveal || mounted ? 1 : 0,
        transition: 'opacity 200ms ease-out',
        maxWidth: '75%',
        minWidth: 0,
      }}
    >
      {senderName ? (
        <span style={{ fontSize: 'var(--chat-fs-label)', color: 'var(--text-dim)', padding: '0 2px', fontFamily: 'var(--font-sans)' }}>
          {senderName}
        </span>
      ) : null}
      {onReact ? (
        <div
          style={{ position: 'relative' }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => { setHover(false); setPickerHover(null); }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 6px)',
              [isYou ? 'right' : 'left']: 0,
              zIndex: 10,
              opacity: hover ? 1 : 0,
              pointerEvents: hover ? 'auto' : 'none',
              transition: 'opacity .12s ease',
            }}
          >
            <div
              style={{
                background: 'var(--surface)', border: '1px solid var(--line)',
                borderRadius: 'var(--r-pill)', padding: '4px 8px',
                display: 'flex', gap: 2, boxShadow: '0 4px 16px rgba(0,0,0,.18)',
              }}
            >
              {reactionOptions.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => onReact(emoji)}
                  onMouseEnter={() => setPickerHover(emoji)}
                  onMouseLeave={() => setPickerHover(null)}
                  style={{
                    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '50%', background: pickerHover === emoji ? 'var(--bg)' : 'transparent',
                    border: 'none', cursor: 'pointer', fontSize: 14, transition: 'background .12s',
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
          {bubble}
        </div>
      ) : bubble}
      {reactionPill}
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isYou ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: 10,
        width: '100%',
        justifyContent: isYou ? 'flex-start' : undefined,
      }}
    >
      {avatar ? (
        <div
          style={{
            flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center',
            ...(avatarMotion === 'drop' && !reduceMotion ? { animation: `sk-avatar-drop 280ms ${REVEAL_EASE} both` } : null),
          }}
          // Once it has settled, let go of the animation.
          onAnimationEnd={avatarMotion === 'drop' ? (e) => { e.currentTarget.style.animation = 'none'; } : undefined}
        >
          {avatarMotion === 'drop' && !reduceMotion ? <style>{AVATAR_KEYFRAMES}</style> : null}
          {avatar}
        </div>
      ) : null}
      {!avatar && reserveGutter ? <div style={{ width: 28, flex: 'none' }} /> : null}
      {column}
    </div>
  );
}

export default MessageBubble;
